"use client";

import { createContext, FunctionComponent, PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";

export interface ThemeProviderProps {
    theme: any;
}

export interface ThemeContextType {
    mode: "light" | "dark";
    toggleMode: (mode: "light" | "dark") => void;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getInitialMode = (): "light" | "dark" => {
    if (typeof window !== "undefined") {
        const savedMode = localStorage.getItem("themeMode") as "light" | "dark" | null;
        if (savedMode === "dark" || savedMode === "light") return savedMode;
    }
    return "light";
};

export const ThemeProvider: FunctionComponent<PropsWithChildren<ThemeProviderProps>> = ({ theme, children }) => {
    const [mode, setMode] = useState<"light" | "dark">(getInitialMode);

    useEffect(() => {
        document.documentElement.classList.remove("light", "dark");
        document.documentElement.classList.add(mode);
        localStorage.setItem("themeMode", mode);
    }, [mode]);

    const toggleMode = useCallback(
        (mode: "light" | "dark" = "light") => {
            setMode(mode);
        },
        [setMode],
    );

    const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

    return (
        <StyledThemeProvider theme={theme}>
            <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
        </StyledThemeProvider>
    );
};
